import React, { useEffect } from 'react';
import { Grid, GridItem, HeadingText40, HeadingText50 } from '@modernhealth/vital-components';
import styled from '@emotion/styled';
import { captureException } from '@sentry/browser';
import { FormattedDateParts, FormattedMessage } from 'react-intl';
import { GraphQLQueryPath } from '@modernhealth/graphql/utils';

import { CircleStylizedSessionCard } from './CircleStylizedSessionCard';
import { FetchSessionsByDateTimeQuery } from '../documents/sessions.generated';

const MonthContainer = styled.section({
  marginBottom: 55,
});

type SessionProps = GraphQLQueryPath<
  FetchSessionsByDateTimeQuery,
  'circleSessionsByDateTime.edges[].node'
>;
export const MonthSection: React.FC<{
  monthStartDate: string;
  sessions: SessionProps[];
  upcomingYear: boolean;
}> = ({ monthStartDate, sessions, upcomingYear }) => {
  const sessionCount = sessions.length;

  useEffect(() => {
    if (sessionCount && sessionCount >= 100) {
      captureException(`Circle sessions were truncated at 100!`, {
        extra: { count: sessionCount },
      });
    }
  }, [sessionCount]);

  if (!sessions || !sessionCount) return null;

  return sessionCount > 0 ? (
    <MonthContainer>
      <FormattedDateParts value={monthStartDate} month="long" year="numeric">
        {value => {
          const month = value.find(value => value.type === 'month');
          const year = value.find(value => value.type === 'year');
          if (!month || !year) return null;

          if (upcomingYear) {
            return (
              <h3
                css={{
                  marginBottom: 24,
                  display: 'flex',
                  flexDirection: 'column-reverse',
                }}
                id="time-label-month-year"
              >
                <HeadingText40 as="span">{month.value}</HeadingText40>
                <HeadingText50 as="span">{year.value}</HeadingText50>
              </h3>
            );
          }

          return (
            <h3
              css={{ marginBottom: 24 }}
              id="time-label-month"
              aria-label={`${month.value}, ${year.value}`}
            >
              <HeadingText40 as="span">{month.value}</HeadingText40>
            </h3>
          );
        }}
      </FormattedDateParts>

      <Grid lgMode="3columns">
        {sessions.map(session => {
          if (!session) return null;

          return (
            <GridItem lg={1} md={2} column key={`session-card-${session.id}`}>
              <CircleStylizedSessionCard key={session.id} session={session}>
                <FormattedMessage defaultMessage="Register" />
              </CircleStylizedSessionCard>
            </GridItem>
          );
        })}
      </Grid>
    </MonthContainer>
  ) : null;
};
